import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Logo } from "../components/Brand";
import { Printer, X } from "lucide-react";

export default function AttorneyPayoff() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useSEO("Payoff letter", "Preliminary payoff statement for an Instabridge funding case.");

  useEffect(() => {
    api.get(`/attorney/payoff/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Payoff not available for this case yet."));
  }, [id]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-6">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 max-w-md text-center">
        <X className="w-10 h-10 text-[#B91C1C] mx-auto mb-3" />
        <h2 className="font-display text-xl text-[#0A2540] font-semibold">Unavailable</h2>
        <p className="text-[#6B7280] mt-2 text-sm">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading…</div>;

  const { application: a, payoff: p } = data;

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-8 print:bg-white print:py-0" data-testid="atty-payoff-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .payoff-doc { box-shadow: none !important; border: none !important; margin: 0 !important; max-width: 100% !important; }
          @page { margin: 0.5in; }
        }
      `}</style>

      <div className="no-print mx-auto max-w-3xl px-6 mb-4 flex justify-between items-center">
        <div className="text-sm text-[#6B7280]">Preliminary payoff statement</div>
        <button onClick={() => window.print()} className="bg-[#0A2540] text-white hover:bg-[#0066FF] px-5 h-10 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors" data-testid="payoff-print-btn">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="payoff-doc mx-auto max-w-3xl bg-white shadow-sm border border-[#E5E7EB] rounded-lg p-10 lg:p-14">
        {/* Header */}
        <div className="flex justify-between items-start pb-6 border-b-2 border-[#0A2540]">
          <div className="flex items-center gap-3">
            <Logo />
            <div>
              <div className="font-display text-xl font-semibold text-[#0A2540]">Instabridge Funding</div>
              <div className="text-xs text-[#6B7280]">Phone: (216) 245-5967 · payoffs@instabridgefunding.com</div>
            </div>
          </div>
          <div className="text-right">
            <div className="uppercase-label">Consumer disclosure</div>
            <div className="text-sm text-[#0A2540] font-medium mt-1">As of {formatDate(p.as_of)}</div>
            <div className="text-xs text-[#6B7280] mt-1">Ref: {a.id.slice(0, 8).toUpperCase()}</div>
          </div>
        </div>

        {/* Parties */}
        <div className="grid grid-cols-2 gap-8 mt-6 text-sm">
          <div>
            <div className="uppercase-label mb-2">Seller (plaintiff)</div>
            <div className="text-[#0A2540] font-semibold text-base">{a.name}</div>
            <div className="text-[#6B7280]">{a.email}</div>
            <div className="text-[#6B7280]">{a.phone}</div>
          </div>
          <div>
            <div className="uppercase-label mb-2">Counsel of record</div>
            <div className="text-[#0A2540] font-semibold text-base">{a.attorney_name}</div>
            <div className="text-[#6B7280]">{a.attorney_email}</div>
            <div className="text-[#6B7280]">{a.attorney_phone}</div>
          </div>
        </div>

        {/* Heading */}
        <div className="mt-8 bg-[#0A2540] text-white py-2 px-4 rounded text-center font-display font-semibold text-lg">
          What Instabridge Legal Funding will own from {a.name.split(" ")[0]}'s Legal Claim
        </div>

        {/* Schedule table */}
        <div className="mt-6">
          <div className="text-sm font-semibold text-[#0A2540] mb-2">1. Total Amount to be repaid by Seller to Instabridge Legal Funding:</div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A2540]">
                <th className="text-left py-2 font-semibold text-[#0A2540]">Start Date</th>
                <th className="text-left py-2 font-semibold text-[#0A2540]"></th>
                <th className="text-left py-2 font-semibold text-[#0A2540]">End Date</th>
                <th className="text-right py-2 font-semibold text-[#0A2540]">Instabridge Ownership Amount</th>
              </tr>
            </thead>
            <tbody>
              {p.schedule.map((r) => (
                <tr key={r.tier} className={`border-b border-[#F3F4F6] ${r.is_current ? "bg-[#FFFBEB]" : ""}`}>
                  <td className="py-2 text-[#374151] tabular-nums">{formatDate(r.start_date)}</td>
                  <td className="py-2 text-[#6B7280] text-xs">to</td>
                  <td className="py-2 text-[#374151] tabular-nums">{formatDate(r.end_date)}</td>
                  <td className="py-2 text-right text-[#0A2540] tabular-nums font-medium">{formatCurrency(r.ownership_amount)}{r.is_current && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#92400E] font-semibold">Current</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 text-center text-sm text-[#374151]">
            Instabridge Legal Funding's Ownership Amount shall <span className="underline font-semibold">never</span> exceed <span className="font-semibold">{formatCurrency(p.ownership_cap)}</span>
          </div>
        </div>

        {/* Numbered terms */}
        <div className="mt-8 space-y-3 text-sm text-[#374151]">
          <NumberedRow n="2." label="Purchase Price to be Paid by Seller:" value={formatCurrency(p.principal)} extra={`Itemized Fees: 0`} />
          <div className="ml-6 text-[#6B7280] flex items-start gap-2"><span>•</span><span>Amount that will go directly to Seller: <span className="font-semibold text-[#0A2540]">{formatCurrency(p.principal)}</span></span></div>
          <NumberedRow n="3." label="Monthly Compounding Fee:" value={`${p.monthly_rate_pct}%`} extra={`(Payment in ${p.tier_months}-month intervals, not month to month)`} />
          <NumberedRow n="4." label={`${p.tier_months}-month percentage fee (based on monthly compounding):`} value={`${p.tier_period_pct}%`} />
          <NumberedRow n="5." label="Annual Percentage Fee (based on monthly compounding):" value={`${p.annual_pct}%`} />
          <NumberedRow n="6." label="Minimum Payment Amount Required:" value={formatCurrency(p.schedule[0].ownership_amount)} />
          <NumberedRow n="7." label="Payment Delivery Fee:" value="$0 – $100.00" extra="See agreement for exact amount." />
        </div>

        {/* Current payoff highlight */}
        <div className="mt-8 p-5 bg-[#0A2540] text-white rounded-lg flex items-center justify-between">
          <div>
            <div className="uppercase-label !text-white/60">Current payoff (as of today)</div>
            <div className="text-xs text-white/70 mt-1">Tier {p.current_tier} of {p.max_tiers} · funded {formatDate(p.funded_at)} · {p.months_elapsed} mo elapsed</div>
          </div>
          <div className="font-display text-3xl font-semibold tabular-nums">{formatCurrency(p.payoff_total)}</div>
        </div>

        {/* Disclosure */}
        <div className="mt-6 p-5 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg text-xs text-[#78350F] leading-relaxed">
          <div className="font-semibold text-[#92400E] mb-1">If Seller {a.name} complies with the nonrecourse purchase agreement and recovers nothing from the applicable legal claim(s), then Purchaser (Instabridge) shall receive nothing.</div>
          This document is generated automatically based on the case-status data on file. The figures above are calculated using your case's specific fee terms ({p.monthly_rate_pct}% monthly compounding, {p.tier_months}-month tiers). A formal stamped payoff letter will be issued upon written request and confirmed within 1 business day. For questions, contact <span className="text-[#0A2540] font-medium">payoffs@instabridgefunding.com</span> or call (216) 245-5967.
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-[#E5E7EB] flex justify-between text-xs text-[#6B7280]">
          <div>Instabridge Legal Funding</div>
          <div>Document generated {formatDate(p.as_of)}</div>
          <div>Page 1 of 1</div>
        </div>
      </div>
    </div>
  );
}

const NumberedRow = ({ n, label, value, extra }) => (
  <div className="flex items-start gap-2">
    <span className="font-semibold text-[#0A2540] w-6">{n}</span>
    <span className="flex-1">{label} <span className="font-semibold text-[#0A2540]">{value}</span></span>
    {extra && <span className="text-xs text-[#6B7280] ml-2">{extra}</span>}
  </div>
);
